import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { getLiveDmNotes, type DmNotesReadError, type LiveDmNotes } from '../services/dmNotes';
import { storeLiveDmNotes } from './dmNotesCache';
import { DM_NOTES_LIVE_KEY } from './queryKeys';

async function fetchLiveNotes(
    queryClient: QueryClient,
    patientId: string,
    signal: AbortSignal
): Promise<LiveDmNotes> {
    const live = await getLiveDmNotes(patientId, signal);
    await storeLiveDmNotes(queryClient, patientId, live, signal);
    return live;
}

// DM sends no webhook for note edits, so the synced copy can be stale until this read lands.
// No retry: a retried hang would keep the editor locked past the timeout; open/refocus re-checks.
export function useLiveDmNotes(patientId: string | undefined) {
    const queryClient = useQueryClient();
    return useQuery<LiveDmNotes, DmNotesReadError>({
        queryKey: DM_NOTES_LIVE_KEY(patientId),
        queryFn: ({ signal }) => fetchLiveNotes(queryClient, patientId!, signal),
        enabled: !!patientId,
        staleTime: 0,
        refetchOnMount: 'always',
        refetchOnWindowFocus: true,
        retry: false
    });
}
